import { Plus, Copy, Trash2, GripVertical } from 'lucide-react';
import clsx from 'clsx';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

export interface Slide {
  id: string;
  title: string;
  content: string;
  template: 'title' | 'content' | 'two-column' | 'image' | 'blank';
}

interface SlidesSidebarProps {
  slides: Slide[];
  currentSlideIndex: number;
  onSelectSlide: (index: number) => void;
  onAddSlide: (template: Slide['template']) => void;
  onDuplicateSlide: (index: number) => void;
  onDeleteSlide: (index: number) => void;
  onReorderSlides: (fromIndex: number, toIndex: number) => void;
}

interface SlideThumbProps {
  slide: Slide;
  index: number;
  isSelected: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  canDelete: boolean;
}

function SlideThumb({
  slide,
  index,
  isSelected,
  onSelect,
  onDuplicate,
  onDelete,
  canDelete,
}: SlideThumbProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slide.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      onClick={onSelect}
      className={clsx(
        'group relative flex gap-2 p-2 rounded-lg cursor-pointer transition-all',
        isSelected ? 'bg-accent-violet/10 ring-2 ring-accent-violet' : 'hover:bg-surface-hover'
      )}
    >
      {/* Drag handle */}
      <div
        {...listeners}
        className="flex items-center text-text-quaternary opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing"
      >
        <GripVertical className="w-4 h-4" />
      </div>

      {/* Slide number */}
      <div className="flex items-center justify-center w-6 text-xs font-medium text-text-quaternary">
        {index + 1}
      </div>

      {/* Thumbnail */}
      <div
        className={clsx(
          'flex-1 aspect-video rounded border overflow-hidden',
          'bg-surface-secondary',
          isSelected ? 'border-accent-violet' : 'border-border-primary'
        )}
      >
        <div className="w-full h-full p-2 flex flex-col items-center justify-center text-center scale-[0.5] origin-center">
          <div className="text-[10px] font-semibold text-text-primary truncate w-full">
            {slide.title || 'Senza titolo'}
          </div>
          {slide.content && (
            <div className="text-[8px] text-text-quaternary truncate w-full mt-1">
              {slide.content.slice(0, 50)}...
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDuplicate();
          }}
          className="p-1 rounded bg-surface-tertiary hover:bg-surface-hover text-text-quaternary hover:text-text-secondary"
          title="Duplica"
        >
          <Copy className="w-3 h-3" />
        </button>
        {canDelete && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="p-1 rounded bg-surface-tertiary hover:bg-accent-rose/10 text-text-quaternary hover:text-accent-rose"
            title="Elimina"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
}

export function SlidesSidebar({
  slides,
  currentSlideIndex,
  onSelectSlide,
  onAddSlide,
  onDuplicateSlide,
  onDeleteSlide,
  onReorderSlides,
}: SlidesSidebarProps) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = slides.findIndex((s) => s.id === active.id);
    const newIndex = slides.findIndex((s) => s.id === over.id);
    if (oldIndex !== -1 && newIndex !== -1) {
      onReorderSlides(oldIndex, newIndex);
    }
  };

  return (
    <div className="w-56 border-r border-border-primary bg-surface-secondary/50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-border-primary">
        <h3 className="text-xs font-semibold text-text-quaternary uppercase tracking-wide">
          Slides
        </h3>
        <span className="text-xs text-text-tertiary bg-surface-tertiary px-1.5 py-0.5 rounded font-medium">
          {slides.length}
        </span>
      </div>

      {/* Slides list */}
      <div className="flex-1 overflow-y-auto p-1.5 space-y-1">
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={slides.map((s) => s.id)} strategy={verticalListSortingStrategy}>
            {slides.map((slide, index) => (
              <SlideThumb
                key={slide.id}
                slide={slide}
                index={index}
                isSelected={index === currentSlideIndex}
                onSelect={() => onSelectSlide(index)}
                onDuplicate={() => onDuplicateSlide(index)}
                onDelete={() => onDeleteSlide(index)}
                canDelete={slides.length > 1}
              />
            ))}
          </SortableContext>
        </DndContext>
      </div>

      {/* Add slide button */}
      <div className="p-2 border-t border-border-primary space-y-1.5">
        <button
          onClick={() => onAddSlide('content')}
          className={clsx(
            'w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-lg',
            'bg-accent-violet text-white hover:bg-accent-violet/90 transition-colors',
            'text-xs font-medium'
          )}
        >
          <Plus className="w-3.5 h-3.5" />
          Aggiungi slide
        </button>

        {/* Template buttons */}
        <div className="grid grid-cols-4 gap-1">
          {(['title', 'content', 'two-column', 'blank'] as const).map((template) => (
            <button
              key={template}
              onClick={() => onAddSlide(template)}
              className={clsx(
                'p-1.5 rounded border border-border-primary bg-surface-tertiary',
                'hover:bg-surface-hover hover:border-accent-violet/50 transition-colors'
              )}
              title={getTemplateName(template)}
            >
              <TemplateIcon template={template} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function getTemplateName(template: Slide['template']): string {
  switch (template) {
    case 'title':
      return 'Solo titolo';
    case 'content':
      return 'Titolo e contenuto';
    case 'two-column':
      return 'Due colonne';
    case 'image':
      return 'Immagine';
    case 'blank':
      return 'Vuota';
  }
}

function TemplateIcon({ template }: { template: Slide['template'] }) {
  const baseClasses = 'w-full aspect-[4/3] rounded-sm';

  switch (template) {
    case 'title':
      return (
        <div
          className={clsx(baseClasses, 'bg-surface-secondary p-1 flex items-center justify-center')}
        >
          <div className="w-3/4 h-1 bg-text-quaternary rounded" />
        </div>
      );
    case 'content':
      return (
        <div className={clsx(baseClasses, 'bg-surface-secondary p-1 flex flex-col gap-0.5')}>
          <div className="w-2/3 h-1 bg-text-quaternary rounded" />
          <div className="flex-1 w-full bg-border-primary rounded" />
        </div>
      );
    case 'two-column':
      return (
        <div className={clsx(baseClasses, 'bg-surface-secondary p-1 flex flex-col gap-0.5')}>
          <div className="w-1/2 h-1 bg-text-quaternary rounded" />
          <div className="flex-1 flex gap-0.5">
            <div className="flex-1 bg-border-primary rounded" />
            <div className="flex-1 bg-border-primary rounded" />
          </div>
        </div>
      );
    case 'image':
      return (
        <div
          className={clsx(baseClasses, 'bg-surface-secondary p-1 flex items-center justify-center')}
        >
          <div className="w-full h-full bg-border-primary rounded" />
        </div>
      );
    case 'blank':
      return (
        <div
          className={clsx(
            baseClasses,
            'bg-surface-secondary border border-dashed border-border-primary'
          )}
        />
      );
  }
}
